/**
 * 蜘蛛の巣の図書館（地図タブ）の試作用ダミーデータ。
 * 本番のデータ（Firestore・管理画面での配置）とは無関係。
 * 世界観: 館を渡り歩くのではなく、一つの無限図書館の中の無限の広がりを散策する（ノード＝図書館の中の場所）。
 *
 * 1階: 中心＝はじまりの閲覧室、放射＝傾向の方向、外側ほど偏りが強い。
 *      隣り合う方向の間に、二つの傾向を組み合わせた館。
 * 2階: 1階では糸が交差してしまう組み合わせ（一つ飛ばしの組・向かい合う組）。
 *      1階の親の館（各方向の2段目）から階段で上がる。2階の中は、タグを1つ共有する館どうしを結ぶ。
 * 3階: 書式の階。2階のどの館からも階段で吹き抜けへ上がり、書式ごとの館へ進む。
 *
 * 組み合わせの館の本は「どちらか片方のタグを満たせばよい」。
 * どの館も「ほかの本」の枠を残し、全作品が出る。
 */

import { GENRE_COLORS, GENRE_LABELS } from "../genre-palette";

export type AttrKey = "fantasy" | "mystery" | "horror" | "romance" | "comedy" | "adventure";
export type FormatKey = "short_story" | "manual" | "grimoire" | "travelogue" | "letter" | "memoir" | "poetry";
export type TagKey = AttrKey | FormatKey;
export type Floor = 1 | 2 | 3;

/** 素材になる傾向（ジャンル）。色と呼び名は精霊と同じ表（genre-palette）から */
export const ATTRS: { key: AttrKey; label: string; color: string }[] = (
  ["fantasy", "mystery", "horror", "romance", "comedy", "adventure"] as const
).map((key) => ({ key, label: GENRE_LABELS[key], color: GENRE_COLORS[key] }));

/** 3階の書式（表記はアプリの novel-labels に合わせる） */
export const FORMATS: { key: FormatKey; label: string; color: string }[] = [
  { key: "short_story", label: "小説",         color: "#e7d8b8" },
  { key: "manual",      label: "手引書",       color: "#94d2bd" },
  { key: "grimoire",    label: "魔導書",       color: "#c9a0dc" },
  { key: "travelogue",  label: "紀行文",       color: "#8ecae6" },
  { key: "letter",      label: "書簡",         color: "#f4a261" },
  { key: "memoir",      label: "自伝・回顧録", color: "#d4a373" },
  { key: "poetry",      label: "詩",           color: "#ffafcc" },
];

export const TAG_BY_KEY = Object.fromEntries(
  [...ATTRS, ...FORMATS].map((t) => [t.key, t]),
) as Record<TagKey, { key: TagKey; label: string; color: string }>;

export const FLOORS: { floor: Floor; label: string }[] = [
  { floor: 1, label: "1層" },
  { floor: 2, label: "2層" },
  { floor: 3, label: "3層" },
];

/** 道を開く素材。any は「どの素材でも合計この数（多い順に使う）」 */
export type Cost = Partial<Record<AttrKey | "any", number>>;

export interface MapNode {
  id: string;
  name: string;
  floor: Floor;
  x: number;
  y: number;
  /** 見た目の大きさの段（0 = 階の中心）。1層では奥行き（はじまりから何本の道か）で、素材の既定値に使う */
  ring: 0 | 1 | 2 | 3;
  /** 段を図面で手で決めた（道のつなぎ方から自動で決め直さない） */
  ringManual?: boolean;
  /** タグごとの排出率（%）。残りはすべての本から均等 */
  weights: Partial<Record<TagKey, number>>;
  cost: Cost;
}

export interface MapEdge {
  from: string;
  to: string;
  /** 階をまたぐ階段（地図には線を引かず、館に印を付ける） */
  stairs?: boolean;
}

/** 1階の輪の半径と、輪ごとの偏り（%）・道を開く素材の数 */
const RING_RADIUS = [0, 170, 320, 470] as const;
const RING_WEIGHT = [0, 30, 40, 45] as const;
const RING_COST = [0, 3, 6, 10] as const;
/** 組み合わせの館（1タグあたり） */
const COMBO1_WEIGHT = 35;
const COMBO1_COST = 4;
const COMBO2_WEIGHT = 45;
const COMBO2_COST = 6;
/** 3階の書式の館 */
const FORMAT_WEIGHT = 45;
const FORMAT_COST_ANY = 12;

/** 放射の方向ごとの館の名前（内側から外側へ） */
const SPOKE_NAMES: Record<AttrKey, [string, string, string]> = {
  fantasy:   ["霧の書架", "月影の回廊", "星図の塔"],
  mystery:   ["鍵のかかった書斎", "暗号の小部屋", "迷宮書庫"],
  horror:    ["夜更けの地下室", "灯の消えた棟", "影の文庫"],
  romance:   ["窓辺の閲覧席", "手紙の間", "薔薇の書庫"],
  comedy:    ["陽だまりの談話室", "落書きの廊下", "道化の劇場文庫"],
  adventure: ["地図の間", "港の書庫", "果ての灯台"],
};

/** 1階: 隣り合う方向 i と i+1 の組 */
const COMBO1_NAMES = [
  "魔術師の研究室", // 幻想×謎
  "開かずの資料室", // 謎×怪異
  "月下の温室",     // 怪異×恋
  "喫茶の本棚",     // 恋×笑い
  "旅芸人の馬車",   // 笑い×冒険
  "竜の書庫",       // 冒険×幻想
];

/** 2階: 一つ飛ばしの組 i と i+2 */
const COMBO2_SKIP_NAMES = [
  "呪われた書架",     // 幻想×怪異
  "探偵の恋文箱",     // 謎×恋
  "お化け屋敷の楽屋", // 怪異×笑い
  "駆け落ちの船着場", // 恋×冒険
  "笑う魔法書の棚",   // 笑い×幻想
  "遺跡の調査室",     // 冒険×謎
];

/** 2階: 向かい合う組 i と i+3 */
const COMBO2_OPPOSITE_NAMES = [
  "妖精の婚礼堂",   // 幻想×恋
  "喜劇の謎解き座", // 謎×笑い
  "肝試しの旅支度", // 怪異×冒険
];

export const START_NODE_ID = "center";
export const FORMAT_HUB_ID = "format-hub";

function polar(radius: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: Math.round(radius * Math.cos(rad)), y: Math.round(radius * Math.sin(rad)) };
}

function spokeAngle(i: number) {
  return -90 + i * (360 / ATTRS.length);
}

function buildMap(): { nodes: MapNode[]; edges: MapEdge[]; decor: MapEdge[] } {
  const nodes: MapNode[] = [
    { id: START_NODE_ID, name: "はじまりの閲覧室", floor: 1, x: 0, y: 0, ring: 0, weights: {}, cost: {} },
  ];
  const edges: MapEdge[] = [];
  /** 見た目だけの線（道ではない。開く判定に使わない） */
  const decor: MapEdge[] = [];
  const n = ATTRS.length;
  const attr = (i: number) => ATTRS[((i % n) + n) % n];

  // ---- 1階 ----
  ATTRS.forEach((a, i) => {
    ([1, 2, 3] as const).forEach((ring) => {
      nodes.push({
        id: `${a.key}-${ring}`,
        name: SPOKE_NAMES[a.key][ring - 1],
        floor: 1,
        ...polar(RING_RADIUS[ring], spokeAngle(i)),
        ring,
        weights: { [a.key]: RING_WEIGHT[ring] },
        cost: { [a.key]: RING_COST[ring] },
      });
    });
    edges.push({ from: START_NODE_ID, to: `${a.key}-1` });
    edges.push({ from: `${a.key}-1`, to: `${a.key}-2` });
    edges.push({ from: `${a.key}-2`, to: `${a.key}-3` });

    const next = attr(i + 1);
    const comboId = `combo-${a.key}-${next.key}`;
    nodes.push({
      id: comboId,
      name: COMBO1_NAMES[i],
      floor: 1,
      ...polar(RING_RADIUS[2] + 10, spokeAngle(i) + 180 / n),
      ring: 2,
      weights: { [a.key]: COMBO1_WEIGHT, [next.key]: COMBO1_WEIGHT },
      cost: { [a.key]: COMBO1_COST, [next.key]: COMBO1_COST },
    });
    // 内側の2方向から入れて、同じ輪の両隣へ抜けられる
    edges.push({ from: `${a.key}-1`, to: comboId });
    edges.push({ from: `${next.key}-1`, to: comboId });
    edges.push({ from: comboId, to: `${a.key}-2` });
    edges.push({ from: comboId, to: `${next.key}-2` });
  });

  // ---- 2階 ----
  // 1階の親の館（2段目）から階段で上がる。2階の中では、タグを1つ共有する館どうしを糸で結ぶ。
  // 一つ飛ばしの組は二つの三角形（隣どうしで1タグ共有）になり、
  // 向かい合う組が内と外の三角形の間の渡りになるので、糸が交差しない。
  const addFloor2 = (id: string, name: string, a: AttrKey, b: AttrKey, pos: { x: number; y: number }) => {
    nodes.push({
      id,
      name,
      floor: 2,
      ...pos,
      ring: 2,
      weights: { [a]: COMBO2_WEIGHT, [b]: COMBO2_WEIGHT },
      cost: { [a]: COMBO2_COST, [b]: COMBO2_COST },
    });
    edges.push({ from: `${a}-2`, to: id, stairs: true });
    edges.push({ from: `${b}-2`, to: id, stairs: true });
    // 3階の吹き抜けへは、2階のどの館からも上がれる
    edges.push({ from: id, to: FORMAT_HUB_ID, stairs: true });
  };
  ATTRS.forEach((a, i) => {
    const b = attr(i + 2);
    addFloor2(`skip-${a.key}-${b.key}`, COMBO2_SKIP_NAMES[i], a.key, b.key, { x: 0, y: 0 });
  });
  [0, 1, 2].forEach((i) => {
    const a = attr(i);
    const b = attr(i + 3);
    addFloor2(`opp-${a.key}-${b.key}`, COMBO2_OPPOSITE_NAMES[i], a.key, b.key, { x: 0, y: 0 });
  });
  // 3本の筋（内→渡り→外）。外の輪へ進むごとに 40° ずつ回し、9館を 40° おきに均等に並べる
  const FLOOR2_LINES: [string, string, string][] = [
    ["skip-fantasy-horror", "opp-horror-adventure", "skip-romance-adventure"],
    ["skip-horror-comedy", "opp-mystery-comedy", "skip-adventure-mystery"],
    ["skip-comedy-fantasy", "opp-fantasy-romance", "skip-mystery-romance"],
  ];
  const FLOOR2_RADIUS = [110, 215, 320];
  FLOOR2_LINES.forEach((line, i) => {
    line.forEach((id, j) => {
      const node = nodes.find((nd) => nd.id === id)!;
      Object.assign(node, polar(FLOOR2_RADIUS[j], -90 + i * 120 + j * 40));
      if (j > 0) edges.push({ from: line[j - 1], to: id });
    });
    // 内の三角形と外の三角形
    const next = FLOOR2_LINES[(i + 1) % FLOOR2_LINES.length];
    edges.push({ from: line[0], to: next[0] });
    edges.push({ from: line[2], to: next[2] });
    // 真ん中の渡りの館どうしはタグを共有しないので、道ではなく軌道の見た目だけを引く
    decor.push({ from: line[1], to: next[1] });
  });

  // ---- 3階（書式） ----
  nodes.push({ id: FORMAT_HUB_ID, name: "書式の吹き抜け", floor: 3, x: 0, y: 0, ring: 0, weights: {}, cost: {} });
  FORMATS.forEach((f, i) => {
    const id = `format-${f.key}`;
    nodes.push({
      id,
      name: `${f.label}の間`,
      floor: 3,
      ...polar(210, -90 + i * (360 / FORMATS.length)),
      ring: 1,
      weights: { [f.key]: FORMAT_WEIGHT },
      cost: { any: FORMAT_COST_ANY },
    });
    edges.push({ from: FORMAT_HUB_ID, to: id });
  });

  return { nodes, edges, decor };
}

export const PROTOTYPE_MAP = buildMap();

/** 最初に持っている素材（道を選ぶ余地が出るよう、少し偏らせてある） */
export const INITIAL_MATERIALS: Record<AttrKey, number> = {
  fantasy: 5,
  mystery: 3,
  horror: 1,
  romance: 4,
  comedy: 2,
  adventure: 1,
};
